import * as Phaser from "phaser-ce"
import { CCUtils, FirebaseEnv } from "../../common/CCUtils"
import { log } from "../../common/Logging"
import { ActivityProgressStoreSA } from "../../react/activity-progress/ActivityProgressStoreSA"
import { ClassStoreSA } from "../../react/classes/ClassStoreSA"
import { StudentStoreSA } from "../../react/students/StudentStoreSA"
import { ActivityKey } from "../../shared/models/ActivityKey"
import { GameState } from "../GameState"
import { PhaserUtils } from "../utils/PhaserUtils"


export enum MapImages {
    BACKGROUND = "map-with-roads-scenery.jpg",

    ADDICTION_MAZE = "addiction-maze.png",
    ARCADE = "arcade.png",
    BIG_HOUSE = "big-house-1.png",
    BIG_HOUSE_2 = "big-house-2.png",
    CAMP_CRAVINGS = "camp-cravings.png",
    NEWS = "cc-news.png",
    CELL_TOWER = "cell-tower.png",
    BUILDING = "click-city-building-1.png",
    BUILDING_2 = "click-city-building-2.png",
    BUILDING_3 = "click-city-building-3.png",
    SIGN = "click-city-sign.png",
    FARM = "farm.png",
    HOSPITAL = "hospital.png",
    HOUSE = "house-1.png",
    HOUSE_2 = "house-2.png",
    HOUSE_3 = "house-3.png",
    KWIT = "kwit.png",
    LIBRARY = "library.png",
    PARK = "park.png",
    SCHOOL = "school.png",
    SCIENCE_MUSEUM = "science-museum.png",
    THEATER = "theater.png",
    TREES = "trees-group.png",
    TURBINES = "wind-turbine.png",

    YELLOW_HIGHLIGHT = "yellow-highlight.png"
}

export enum MapVideo {
    CONGRATS = "CONGRATS"
}

const welcomeVideos = [
    ActivityKey.ECIG_101, ActivityKey.KIDS_CHOICE, ActivityKey.MAKE_YOUR_OWN_VAPER_PT_2, ActivityKey.CAMP_CRAVINGS, ActivityKey.SECOND_HAND,
    ActivityKey.MAKE_A_VIDEO, ActivityKey.DEFINITION, ActivityKey.TIME_MACHINE, ActivityKey.ECIG_201, ActivityKey.ADDICTION_201
]

const demoWelcomeVideos = [ActivityKey.ECIG_101, ActivityKey.SECOND_HAND]

/**
 * This loadAssets function will initially check if the user is in the demo environment. If true, then all demo welcome videos will be loaded.
 * For other environments:
 * - If the current user is an admin, load all welcome videos.
 * - If the user is a student, load video according to the student's progress in the game.
 * All other assets are autoloaded during preload.
 * @param game
 */
export function loadAssets(game: Phaser.Game) {
    const load = PhaserUtils.makeLoader(game, "map")
    load.autoLoad({imageEnum: MapImages})

    const activityToFolderName = (activity: GameState) => activity.toLowerCase().replace(/_/g, "-")

    const loadOneTransitionAudio = (activity: GameState) => {
        log.debug("Loading transition audio for:", activity)
        game.load.audio(
            transitionAudioKey(activity),
            CCUtils.asset(CCUtils.genderifyFileName(`map/${activityToFolderName(activity)}/audio/transition.mp3`))
        )
    }

    const loadAllTransitionAudios = () => {
        // Add transition audio for before each activity
        Object.values(ActivityKey).forEach((activity) => {
            loadOneTransitionAudio(activity)
        })
    }

    const loadAllVideos = activity => {
        const videoLocation = CCUtils.asset(CCUtils.genderifyFileName(`map/${activityToFolderName(activity)}/video/welcome.mp4`))
        game.load.video(welcomeVideoKey(activity), videoLocation)
    }

    if (CCUtils.firebaseEnv() === FirebaseEnv.DEMO) {
        demoWelcomeVideos.forEach(loadAllVideos)
        loadAllTransitionAudios()
    } else {
        if (StudentStoreSA.instance.isAdmin() || CCUtils.debug) {
            welcomeVideos.forEach(loadAllVideos)
            loadAllTransitionAudios()
        } else {
            const activityKey = ActivityProgressStoreSA.instance.getNextActivityKey()
            if (activityKey !== "fireworks" && welcomeVideos.includes(activityKey)) {
                const videoLocation = CCUtils.asset(CCUtils.genderifyFileName(`map/${activityToFolderName(activityKey)}/video/welcome.mp4`))
                game.load.video(welcomeVideoKey(activityKey), videoLocation)
            }
            if (activityKey !== "fireworks") {
                loadOneTransitionAudio(activityKey)
            }
        }
    }
    if (ActivityProgressStoreSA.instance.activitiesComplete()) {
        const congratsVideo = CCUtils.asset(CCUtils.genderifyFileName(
            `map/fireworks/video/congratulations${ClassStoreSA.instance.is5thGrade() ? "" : "-6"}.mp4`
        ))
        game.load.video(MapVideo.CONGRATS, congratsVideo)
    }
}

export function welcomeVideoKey(activity: string) {
    return activity + "welcomevideo"
}

export function transitionAudioKey(activity: string) {
    return activity + "transitionaudio"
}
